import {
	GroupLabelRequest,
	GroupLabel,
	GroupLabelUpdateRequest,
	SearchGroupLabelResponse,
	SearchGroupLabelsPayload,
} from "@/domain/groupLabel.interface";
import { instanceBo, axios, transformAxiosError } from "../utils/axios.create";
import {
	OrganizationItem,
	OrganizationsByRolPayload,
	OrganizationResponse,
	ChildOrganization,
} from "@/domain/organization.interface";
import {
	OrganizationLevelResponse,
	IOrganizationLevel,
} from "@/domain/organizationLevel.interface";

export interface OrganizationRequest {
	accountId: string;
	name: string;
	description?: string;
	status: string;
	ouLevel?: number;
	address: {
		address1: string;
		address2?: string;
		city: string;
		state: string;
		zipCode: string;
	};
	contact: {
		name: string;
		phoneNumber: string;
	};
	additionalInfo: {
		approvedDate: string;
		npi: string;
	};
	parentOrgUnitId?: string;
	groupLabelIds: string[];
	defaultCupId?: string;
	createdBy: string;
}
/* `SearchGroupLabelsPayload` is a type that represents the payload structure used for
searching group labels. It includes properties such as `search` for the search query,
`sortField` for the field to sort by, `sortOrder` for the sorting order, `pageNumber`
for the current page number, and `pageSize` for the number of items per page. This type
is used to define the structure of the payload that can be sent when searching for group
labels in the application. */

export interface OrganizationUpdateRequest
	extends Omit<OrganizationRequest, "createdBy"> {
	_id: string;
	updatedBy: string;
}

export interface OrganizationsResponse {
	statusCode: number;
	body: OrganizationResponse[];
}

export interface OrganizationLevelRequest {
	name: string;
	level: number;
	createdBy: string;
}

export interface OrganizationLevelUpdateRequest
	extends Omit<OrganizationLevelRequest, "createdBy"> {
	_id: string;
	updatedBy: string;
}

const findAll = async (): Promise<OrganizationResponse[]> => {
	return instanceBo
		.get("/organizations")
		.then((res) => res.data.body)
		.catch((err) => {
			throw new Error(err.response.data);
		});
};

const findOrganizationsByRoleId = async (
	accountId: string,
	roleId: string,
): Promise<OrganizationsByRolPayload> => {
	return instanceBo
		.get(`/organizations-parent-level?roleId=${roleId}`)
		.then((res) => {
			const organizationsByLevelPayload = res.data
				.body as OrganizationsByRolPayload;

			return organizationsByLevelPayload;
		})
		.catch((err) => {
			return Promise.reject(new Error(err.response.data));
		});
};

const findOrganizationsByAccountProgramAndParentOrg = async (
	accountId: string,
	programId: string,
	parentOrgId: string,
): Promise<ChildOrganization[]> => {
	const url = "/organizations?";
	const parentOrgIdParam = parentOrgId ? `&parentOrgUnitId=${parentOrgId}` : "";
	const fullUrl = url + `programId=${programId}` + parentOrgIdParam;

	return instanceBo
		.get(fullUrl)
		.then((res) => {
			let organizations = res.data.body as OrganizationItem[];

			if (!parentOrgId) {
				organizations = organizations.filter(
					(organization: OrganizationItem) => organization.ouLevel === 0,
				);
			}

			return organizations.map((organization: OrganizationItem) => {
				const { id, name } = organization;
				return {
					id,
					name,
				};
			});
		})
		.catch((err) => {
			return Promise.reject(new Error(err.response.data));
		});
};

const getOrganizationLevels = async (): Promise<OrganizationLevelResponse> => {
	return instanceBo
		.get("/organization-levels")
		.then((res) => res.data)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const createOrganizationLevel = async (
	data: OrganizationLevelRequest,
): Promise<IOrganizationLevel> => {
	return instanceBo
		.post("/organization-levels", data)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const updateOrganizationLevel = async (
	data: OrganizationLevelUpdateRequest,
): Promise<IOrganizationLevel> => {
	const { _id, ...updateData } = data;
	return instanceBo
		.put(`/organization-levels/${_id}`, updateData)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const deleteOrganizationLevel = async (id: string): Promise<void> => {
	return instanceBo
		.delete(`/organization-levels/${id}`)
		.then(() => undefined)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const getOrganizations = async (): Promise<OrganizationsResponse> => {
	return instanceBo
		.get("/organizations")
		.then((res) => res.data)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const createOrganization = async (
	data: OrganizationRequest,
): Promise<OrganizationResponse> => {
	return instanceBo
		.post("/organizations", data)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const createGroupLabel = async (
	data: GroupLabelRequest,
): Promise<GroupLabel> => {
	return instanceBo
		.post("/organizations/group-labels", data)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const updateOrganization = async (
	data: OrganizationUpdateRequest,
): Promise<OrganizationResponse> => {
	const { _id, ...updateData } = data;
	return instanceBo
		.put(`/organizations/${_id}`, updateData)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const deleteOrganization = async (id: string): Promise<void> => {
	return instanceBo
		.delete(`/organizations/${id}`)
		.then(() => undefined)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const deleteGroupLabel = async (id: string): Promise<void> => {
	return instanceBo
		.delete(`/organizations/group-labels/${id}`)
		.then(() => undefined)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const getGroupLabels = async (): Promise<GroupLabel[]> => {
	return instanceBo
		.get("/organizations/group-labels")
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const updateGroupLabel = async (
	data: GroupLabelUpdateRequest,
): Promise<GroupLabel> => {
	const { _id, ...updateData } = data;
	return instanceBo
		.put(`/organizations/group-labels/${_id}`, updateData)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data || err);
		});
};

const findGroupLabelsAll = async ({
	search,
	sortField,
	sortOrder,
	pageNumber,
	pageSize,
}: SearchGroupLabelsPayload): Promise<SearchGroupLabelResponse> => {
	const queryParams = new URLSearchParams({
		pageNumber: String(pageNumber + 1),
		pageSize: String(pageSize),
		search,
		sortField,
		sortOrder: sortOrder === 1 ? "asc" : "desc",
	});

	return axios
		.get(`/organizations/group-labels/search?${queryParams.toString()}`)
		.then((res) => {
			const body = res.data.body;
			return {
				organizations: body.organizations,
				totalCount: body.totalCount,
			};
		})
		.catch((err) => {
			return Promise.reject(transformAxiosError(err));
		});
};

export const organizationService = {
	findAll,
	findOrganizationsByRoleId,
	findOrganizationsByAccountProgramAndParentOrg,
	getOrganizationLevels,
	createOrganizationLevel,
	updateOrganizationLevel,
	deleteOrganizationLevel,
	getOrganizations,
	createOrganization,
	updateOrganization,
	deleteOrganization,
	createGroupLabel,
	updateGroupLabel,
	deleteGroupLabel,
	getGroupLabels,
	findGroupLabelsAll,
};
